/**
 * Registro de Pagos Ventas Generales — migración web de ares9650
 * ("Registro de Pagos de GPS").
 *
 * Cobra ventas generales pendientes: se selecciona el cliente (por préstamo,
 * cédula o número de GPS — el backend devuelve todas las ventas con saldo y
 * aquí se filtra localmente), se elige la factura y se aplica el pago
 * (INSERT en pagosgps + acumula gps.cobrado). El pago no puede exceder el
 * saldo. Transferencias exigen referencia. Al guardar se imprime el recibo
 * con el balance pendiente (una sola vez — el escritorio lo imprimía doble).
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";

const filtrarPendientes = createFilterOptions({
  stringify: (o) =>
    typeof o === "string"
      ? o
      : `${o.noprest || ""} ${o.nombres || ""} ${o.cedula || ""} ${o.gps || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Seleccione un cliente con ventas pendientes
      </Typography>
    </Stack>
  );
}

// Recibo del pago (equivalente web de imprimirpagosgps del escritorio).
export function imprimirReciboPagoVentaGeneral(r) {
  const fila = (k, v) =>
    `<tr><td style="padding:3px 8px;color:#555">${k}</td><td style="padding:3px 8px;font-weight:bold">${
      v || ""
    }</td></tr>`;
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Recibo de Pagos Ventas Miscelaneas</title></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:13px;max-width:420px;margin:0 auto">
      <h3 style="text-align:center;margin:0 0 2px">${r.compania?.nombre || ""}</h3>
      <p style="text-align:center;margin:0">${r.compania?.direccion || ""}</p>
      <p style="text-align:center;margin:0 0 14px">${
        r.compania?.rnc ? "RNC: " + r.compania.rnc : ""
      }</p>
      <p style="text-align:center;margin:0 0 14px;font-weight:bold">Recibo de Pagos Ventas Miscelaneas</p>
      <table style="width:100%;border-collapse:collapse">
        ${r.norecibo ? fila("Recibo #", r.norecibo) : ""}
        ${fila("Fecha", r.fecha)}
        ${fila("Tipo Ingreso", r.tipogasto)}
        ${r.noprest ? fila("# Préstamo", r.noprest) : ""}
        ${r.gps ? fila("GPS", r.gps) : ""}
        ${fila("Factura #", r.nfactura)}
        ${fila("Cédula", r.cedula)}
        ${fila("Nombres", r.nombres)}
        ${fila("Valor", "RD$ " + fmtMoney(r.valor))}
        ${r.formapago ? fila("Forma de pago", r.formapago) : ""}
        ${r.referencia ? fila("Referencia", r.referencia) : ""}
        ${fila("Balance Pendiente", "RD$ " + fmtMoney(r.balancependiente))}
      </table>
      <p style="margin-top:34px;border-top:1px solid #000;padding-top:6px;text-align:center">Preparado Por</p>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

// Imprime un recibo ya registrado a partir de su número. Lo usan la reimpresión
// y el cobro de pagos, cuyo endpoint solo devuelve el norecibo.
export async function imprimirReciboVentaGeneralPorNumero(post, norecibo) {
  const { ok, data } = await post("/api/ventageneral_pago_recibo", { norecibo });
  if (!ok) return { ok: false, error: data?.Error || "No se pudo recuperar el recibo" };
  const r = data.recibo;
  imprimirReciboPagoVentaGeneral({
    compania: data.compania,
    norecibo: r.norecibo,
    fecha: r.fecha,
    tipogasto: r.tipogasto,
    noprest: r.noprest || "",
    gps: r.gps || "",
    nfactura: r.nfactura,
    cedula: r.cedula,
    nombres: r.nombres,
    valor: r.vpagcap,
    formapago: r.banco,
    referencia: r.referencia,
    balancependiente: r.balanceanterior,
  });
  return { ok: true };
}

export default function PagosVentasGenerales({ embedded = false, onExit, onSaved } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [pendientes, setPendientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [sel, setSel] = useState(null);
  const [factura, setFactura] = useState(null);
  const [valor, setValor] = useState("");
  const [formapago, setFormapago] = useState("Contado");
  const [referencia, setReferencia] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/ventageneral_pendientes", {});
      if (ok) setPendientes(data.pendientes || []);
      else showAlert({ message: data.Error || "No se pudieron cargar las ventas", color: "error" });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Facturas del cliente elegido: por préstamo si lo tiene, si no por
  // cédula + gps (mismas agrupaciones que los dos modos del escritorio).
  const facturas = useMemo(() => {
    if (!sel) return [];
    if (sel.noprest) return pendientes.filter((p) => p.noprest === sel.noprest);
    return pendientes.filter(
      (p) => !p.noprest && p.cedula === sel.cedula && (p.gps || "") === (sel.gps || "")
    );
  }, [sel, pendientes]);

  const totalPendiente = useMemo(
    () => facturas.reduce((acc, f) => acc + toNum(f.saldo), 0),
    [facturas]
  );

  const limpiar = () => {
    setSel(null);
    setFactura(null);
    setValor("");
    setFormapago("Contado");
    setReferencia("");
  };

  const pagar = async () => {
    if (!factura) {
      showAlert({ message: "Debe seleccionar una factura de GPS", color: "warning" });
      return;
    }
    const monto = toNum(valor);
    if (monto <= 0) {
      showAlert({ message: "El valor de la factura no puede estar en 0", color: "warning" });
      return;
    }
    if (monto > toNum(factura.saldo)) {
      showAlert({ message: "El valor excede de la deuda pendiente", color: "warning" });
      return;
    }
    if (formapago !== "Contado" && !referencia.trim()) {
      showAlert({ message: "Debe indicar la referencia de la transferencia", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/ventageneral_pagar", {
        id: factura.id,
        valor: monto,
        formapago,
        referencia: referencia.trim(),
      });
      if (ok) {
        showAlert({ message: data.ok || "Pago registrado", color: "success" });
        imprimirReciboPagoVentaGeneral({
          compania: data.compania,
          norecibo: data.norecibo,
          fecha: data.fecha,
          tipogasto: factura.tipogasto,
          noprest: factura.noprest || "",
          gps: factura.gps || "",
          nfactura: factura.id,
          cedula: factura.cedula,
          nombres: factura.nombres,
          valor: monto,
          formapago,
          referencia,
          balancependiente: data.balancependiente,
        });
        limpiar();
        if (onSaved) onSaved();
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo registrar el pago", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  const columns = [
    { field: "id", headerName: "Fact#", width: 90 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    { field: "tipogasto", headerName: "Categoría", width: 140 },
    { field: "tipofactura", headerName: "Tipo", width: 100 },
    { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 140 },
    {
      field: "saldo",
      headerName: "Saldo",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "acciones",
      headerName: "",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          onClick={() => {
            setFactura(params.row);
            setValor(String(toNum(params.row.saldo).toFixed(2)));
          }}
          sx={btnOutline(FP.color.navy)}
        >
          Seleccionar
        </Button>
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Registrar Pagos Ventas Generales"
        subtitle="Aplica cobros a las ventas generales pendientes"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Cliente */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PersonSearchOutlinedIcon} color={FP.color.accentBlue}>
            Cliente
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarPendientes}
                options={pendientes}
                loading={cargando}
                value={sel}
                getOptionLabel={(o) =>
                  typeof o === "string"
                    ? o
                    : `${o.nombres || ""} — ${o.noprest ? `Préstamo ${o.noprest}` : o.cedula || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.id || o) === (v.id || v)}
                onChange={(_, v) => {
                  setSel(v);
                  setFactura(null);
                  setValor("");
                }}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.nombres}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[
                          o.noprest ? `Préstamo ${o.noprest}` : "",
                          o.cedula,
                          o.gps ? `GPS ${o.gps}` : "",
                          o.tipogasto,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Préstamo, nombre, cédula o número de GPS"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
              {sel && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={`Cédula ${sel.cedula || "—"}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`Total pendiente RD$ ${fmtMoney(totalPendiente)}`}
                    sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                  />
                </Stack>
              )}
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={facturas}
              columns={columns}
              loading={cargando}
              rowHeight={46}
              pageSizeOptions={[10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "180px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>

        {/* Pago */}
        {factura && (
          <Box sx={{ ...sectionBox, mb: 0 }}>
            <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>
              Pago — Factura #{factura.id}
            </SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Pendiente"
                  value={fmtMoney(factura.saldo)}
                  InputProps={{ readOnly: true }}
                  inputProps={{ style: { textAlign: "right" } }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Valor a pagar"
                  value={valor}
                  onChange={(e) => setValor(e.target.value)}
                  placeholder="0.00"
                  inputProps={{ style: { textAlign: "right" } }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  select
                  fullWidth
                  label="Forma de pago"
                  value={formapago}
                  onChange={(e) => setFormapago(e.target.value)}
                  sx={fpInputSx}
                >
                  <MenuItem value="Contado">Contado</MenuItem>
                  <MenuItem value="Transferencia">Transferencia</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Referencia"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  helperText={
                    formapago !== "Contado" ? "Obligatoria para transferencias" : undefined
                  }
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  startIcon={
                    guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                  }
                  onClick={pagar}
                  disabled={guardando}
                  sx={btnSolid(FP.color.navy, FP.color.navySoft)}
                >
                  Registrar pago e imprimir
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
}
